import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import cookieManager from "../CookieManager";
import Constants from "../Constants";
import UserAgent from "../Utils/UserAgent";
import {getLanguage} from "../../screens/Game/utils/UrlParams";
import LocalizationRepository from "./LocalizationRepository";

class Localize {
    static instance = null;
    static initialized = false;
    static supportedLanguages = ["en", "es", "de", "fr", "it", "pt"];
    static defaultLanguage = "en";
    static namespace = "translation";
    static loadedLanguages = new Set();

    static async init() {
        if (!Localize.instance) {
            // Try to get language from cookies
            let defaultLanguage = cookieManager.get(Constants.LANGUAGE) || this.getDefaultLanguage();

            const paramLanguage = getLanguage();
            if (paramLanguage !== null) {
              defaultLanguage = paramLanguage;
            }

            if (!this.isSupported(defaultLanguage)) {
              defaultLanguage = this.defaultLanguage;
            }

            Localize.instance = i18next.use(initReactI18next);

            await Localize.instance.init({
                lng: defaultLanguage, // Language from cookie or default to 'en'
                fallbackLng: this.defaultLanguage,
                debug: false,
                // Some export keys contain dots (KEY_deposit.RealMoney), so '.' and ':' must
                // not be read as key/namespace separators.
                keySeparator: false,
                nsSeparator: false,
                resources: {},
                interpolation: { escapeValue: false },
            });

            Localize.initialized = true;

            // The selected language plus English, which is what 'fallbackLng' resolves against.
            await Promise.all([
                this.loadLanguageBundle(defaultLanguage),
                this.loadLanguageBundle(this.defaultLanguage),
            ]);
        }
    }

    static async loadLanguageBundle(language) {
        if (Localize.loadedLanguages.has(language)) {
            return;
        }

        const strings = await LocalizationRepository.getStrings(language);

        if (!strings || Object.keys(strings).length === 0) {
            // Left out of loadedLanguages so a later call can retry the download.
            console.error(`[Localize] No localizations available for ${language}`);
            return;
        }

        Localize.instance.addResourceBundle(language, Localize.namespace, strings, true, true);
        Localize.loadedLanguages.add(language);
    }
    
    static getDefaultLanguage() {
      const browserLanguage = (navigator.language || this.defaultLanguage).slice(0, 2).toLowerCase();
      return this.isSupported(browserLanguage) ? browserLanguage : this.defaultLanguage;
    }

    static async setLanguage(lng) {
        if (!Localize.initialized) {
            throw new Error("[Localize] Not initialized. Call Localize.init() first.");
        }

        lng = this.isSupported(lng) ? lng : this.defaultLanguage;

        await this.loadLanguageBundle(lng);

        return new Promise((resolve, reject) => {
            Localize.instance.changeLanguage(lng, (err) => {
                if (err) {
                    console.error("[Localize] Error changing language:", err);
                    return reject(err);
                }
                cookieManager.set(Constants.LANGUAGE, lng);
                console.log(`[Localize] Language changed to ${lng}`);
                resolve();
            });
        });
    }

    static translate(key, options) {
        if (!Localize.initialized) {
            throw new Error("[Localize] Not initialized. Call Localize.init() first.");
        }
        return Localize.instance.t(key, options);
    }

    static translateToUpper(key, options) {
        if (!Localize.initialized) {
            throw new Error("[Localize] Not initialized. Call Localize.init() first.");
        }
        const text = Localize.instance.t(key, options);
        return typeof text === "string" ? text.toUpperCase() : text;
    }

    static isSupported(lang) {
        return this.supportedLanguages.includes(lang);
    }

    static exists(key) {
        if (!Localize.initialized) {
            throw new Error("[Localize] Not initialized. Call Localize.init() first.");
        }
        return Localize.instance.exists(key);
    }
    
    static translateByBrowserOrFallback(prefixKey, replacements = {}) {
        const token = UserAgent.getBrowserName().toUpperCase();
        const browserKey = `${prefixKey}_${token}`;

        const keyToUse = this.exists(browserKey) ? browserKey : prefixKey;
        return this.translateAndFormat(keyToUse, replacements);
    }

    static translateAndFormat(text, replacements = {}) {
        // Translate the text
        text = this.translate(text);

        // Replace newlines (\n) with <br>
        text = text.replace(/\n/g, "<br></br>");

        Object.entries(replacements).forEach(([key, value]) => {
            text = text.replace(key, value);
        });

        // Split by HTML-ish tags
        const regex = /(<\/?[a-z][^>]*>)/g;
        const parts = text.split(regex);

        // Stack for nested tags
        const stack = [];

        return parts.map((part, index) => {
            if (part.match(/^<\w+>$/)) {
                const tag = part.replace(/[<>]/g, "");
                stack.push(tag);
                return null;
            } else if (part.match(/^<\/\w+>$/)) {
                stack.pop();
                return null;
            } else {
                let content = <span key={`content-${index}`}>{part}</span>;

                stack.slice().reverse().forEach((tag) => {
                    if (tag === "b") {
                        content = <strong key={`strong-${tag}-${index}`}>{content}</strong>;
                    } else if (tag === "i") {
                        content = <em key={`em-${tag}-${index}`}>{content}</em>;
                    } else if (tag === "u") {
                        content = <u key={`u-${tag}-${index}`}>{content}</u>;
                    } else if (tag === "br") {
                        content = (
                            <>
                                {content}
                                <br key={`br-${tag}-${index}`} />
                            </>
                        );
                    } else {
                        content = <span key={`span-${tag}-${index}`}>{content}</span>;
                    }
                });

                return content;
            }
        });
    }

    static async setLanguageFromBrowser() {
        if (!Localize.initialized) {
            throw new Error("[Localize] Not initialized. Call Localize.init() first.");
        }

        const browserLanguage = (navigator.language || "en").slice(0, 2).toLowerCase();
        const selectedLanguage = this.isSupported(browserLanguage) ? browserLanguage : this.defaultLanguage;

        console.log(`[Localize] Setting language by browser: ${selectedLanguage}`);

        await Localize.setLanguage(selectedLanguage);
    }

    static async getLanguageByCountry(country) {
        switch (country) {
            case "US":
            case "GB":
            default:
                return "en";
            case "ES":
                return "es";
            case "DE":
                return "de";
            case "FR":
                return "fr";
            case "IT":
                return "it";
            case "PT":
            case "BR":
                return "pt";
        }
    }
}

export default Localize;
